import { useContext, useMemo } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { FeedbackContext } from '~/components/feedback'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { getApiErrorCode } from '../helpers/advertiserBrandErrors'
import { settleBatch } from '../helpers/settleBatch'
import {
  createAdvertiserBrandService,
  deleteAdvertiserBrandService,
  listAdvertiserBrandsService,
  renameAdvertiserBrandService
} from '../services/advertiser-brand'
import {
  ADVERTISER_BRAND_LIST_LIMIT,
  advertiserBrandKey,
  advertiserBrandsQueryKey,
  invalidateAdvertiserBrandQueries
} from './advertiserBrandQueryKeys'

/** Códigos do import (e o `INVALID_GTIN` compartilhado): mensagem única em `import.errors`. */
const IMPORT_ERROR_CODES: ReadonlySet<string> =
  new Set<AdvertiserBrandImportErrorCode>([
    'MISSING_HEADERS',
    'EMPTY_FILE',
    'TOO_MANY_ROWS',
    'INVALID_GTIN',
    'MISSING_BRAND',
    'DUPLICATE_ROW',
    'EAN_NOT_IN_CATALOG'
  ])

/**
 * Traduz os códigos de negócio do contrato (`BRAND_ALREADY_EXISTS`,
 * `BRAND_NOT_FOUND`, `PRODUCTS_REQUIRED`, `NTB_NOT_ENABLED` e os do import),
 * caindo na mensagem genérica quando o erro não é conhecido.
 */
export const useAdvertiserBrandErrorMessage = () => {
  const { t } = useAPPTranslation()

  return (error: unknown) => {
    const code = getApiErrorCode(error)
    const fallback = t('c:messages.errorOccurred.message')

    if (!code) return fallback

    if (IMPORT_ERROR_CODES.has(code)) {
      return t(`rm:advertiserProducts.import.errors.${code}`, {
        defaultValue: fallback
      })
    }

    return t(`rm:advertiserBrands.errors.${code}`, { defaultValue: fallback })
  }
}

export const useAdvertiserBrands = (
  params: ListAdvertiserBrandsRequest,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const { isPending, isFetching, data, error } = useQuery({
    queryKey: advertiserBrandKey(advertiserBrandsQueryKey, params),
    enabled,
    queryFn: async () => {
      const response = await listAdvertiserBrandsService({
        ...params,
        count: true
      })

      return response.data
    },
    placeholderData: previousData => previousData
  })

  return { isPending, isFetching, data, error }
}

/** Marcas do anunciante como opções de select (sem paginação). */
export const useAdvertiserBrandOptions = () => {
  const { data, isPending } = useQuery({
    queryKey: advertiserBrandKey(advertiserBrandsQueryKey, 'options'),
    queryFn: async () => {
      const response = await listAdvertiserBrandsService({
        quantity: ADVERTISER_BRAND_LIST_LIMIT,
        count: false
      })

      // com count=false a API devolve o array puro; o envelope é tolerado
      const body = response.data as
        | AdvertiserBrand[]
        | ListAdvertiserBrandsResponse

      return Array.isArray(body) ? body : (body?.data ?? [])
    },
    staleTime: 60 * 1000
  })

  // referência estável entre renders — consumido em deps de useMemo/useEffect
  const options = useMemo(
    () => (data ?? []).map(brand => ({ label: brand.name, value: brand.name })),
    [data]
  )

  return { isPending, brands: data ?? [], options }
}

/**
 * Escritas da tela "Minhas marcas". Toda mutação invalida a listagem de marcas
 * e a de vínculos (a contagem por marca vem do mesmo agregado).
 */
export const useAdvertiserBrandMutations = () => {
  const { t } = useAPPTranslation()
  const { toast } = useContext(FeedbackContext)
  const queryClient = useQueryClient()
  const getErrorMessage = useAdvertiserBrandErrorMessage()

  const invalidate = () => invalidateAdvertiserBrandQueries(queryClient)

  const createBrand = useMutation({
    mutationFn: async (body: CreateAdvertiserBrandRequest) => {
      const response = await createAdvertiserBrandService(body)

      return response.data
    },
    onSuccess: data => {
      invalidate()
      toast.success(
        t('rm:advertiserBrands.create.success', {
          brand: data.brand,
          count: data.created
        })
      )
    },
    onError: error => toast.critical(getErrorMessage(error))
  })

  const renameBrand = useMutation({
    mutationFn: async (body: RenameAdvertiserBrandRequest) => {
      const response = await renameAdvertiserBrandService(body)

      return response.data
    },
    onSuccess: data => {
      invalidate()
      toast.success(
        t('rm:advertiserBrands.rename.success', { brand: data.brand })
      )
    },
    onError: error => toast.critical(getErrorMessage(error))
  })

  /** Uma request por marca; o lote parcial vira aviso, não sucesso nem erro. */
  const deleteBrand = useMutation({
    mutationFn: (brands: string[]) =>
      settleBatch(
        brands.map(async brand => {
          await deleteAdvertiserBrandService(brand)

          return brand
        })
      ),
    onSuccess: ({ done, total, failed }) => {
      if (failed) {
        toast.warning(
          t('rm:advertiserCatalog.batch.partial', { done: done.length, total })
        )

        return
      }

      toast.success(
        t('rm:advertiserBrands.delete.success', {
          count: done.length,
          brand: done[0]
        })
      )
    },
    onError: error => toast.critical(getErrorMessage(error)),
    onSettled: invalidate
  })

  return { createBrand, renameBrand, deleteBrand }
}
